import { defineStore } from "pinia";
import type UserProfileBasedSecurity from "@/interfaces/Security/UserProfileBasedSecurity";
import type WebLogin from "@/interfaces/WebLogin/WebLogin";
import type WebLoginAccount from "@/interfaces/WebLogin/WebLoginAccount";
import type WebLoginAuthenticationTokens from "@/interfaces/WebLogin/WebLoginAuthenticationTokens";
import WebLoginAccountFactory from "@/factories/WebLogin/WebLoginAccountFactory";
import { getWebLoginFromJsonWebToken } from "@/helpers/WebLogin/WebLoginHelpers";
import { getUserProfileBasedSecurity } from "@/repositories/Security/UserProfileBasedSecurityRepository";
import { useParametersStore } from "@/stores/parameters";

function clearStorageKeys() {
  localStorage.removeItem("tokens");
  sessionStorage.removeItem("tokens");
  localStorage.removeItem("selectedAccount");
  sessionStorage.removeItem("selectedAccount");
  localStorage.removeItem("userProfileBasedSecurity");
  sessionStorage.removeItem("userProfileBasedSecurity");
}

function getStorage(keepLoggedIn: boolean) {
  return keepLoggedIn ? localStorage : sessionStorage;
}

function parseStoredJson<T>(serializedValue: string, key: string): T {
  try {
    return JSON.parse(serializedValue) as T;
  } catch (error) {
    throw new Error(
      `Stored ${key} is invalid JSON: ${
        error instanceof Error ? error.message : "Unknown parsing error."
      }`
    );
  }
}

export const useAuthStore = defineStore("auth", {
  state: () => ({
    selectedAccount: undefined as WebLoginAccount | undefined,
    webLogin: {} as WebLogin,
    tokens: undefined as WebLoginAuthenticationTokens | undefined,
    userProfileBasedSecurity: undefined as UserProfileBasedSecurity | undefined,
    sessionRestored: false,
  }),
  getters: {
    isAuthenticated(state) {
      return state.tokens !== undefined;
    },
  },
  actions: {
    persistSessionState() {
      clearStorageKeys();

      if (!this.tokens) {
        return;
      }

      const storage = getStorage(this.webLogin.keepLoggedIn);

      storage.setItem("tokens", JSON.stringify(this.tokens));

      if (this.selectedAccount) {
        storage.setItem("selectedAccount", JSON.stringify(this.selectedAccount));
      }

      if (this.userProfileBasedSecurity) {
        storage.setItem(
          "userProfileBasedSecurity",
          JSON.stringify(this.userProfileBasedSecurity)
        );
      }
    },
    setWebTokens(tokens: WebLoginAuthenticationTokens | undefined) {
      if (!tokens) {
        this.tokens = undefined;
        this.webLogin = {} as WebLogin;
        this.persistSessionState();
        return;
      }

      const webLogin = getWebLoginFromJsonWebToken(tokens.jsonWebToken);

      this.tokens = { ...tokens };
      this.webLogin = webLogin;
      this.persistSessionState();
    },
    async setSelectedAccount(
      selectedAccount: WebLoginAccount | undefined,
      reloadEntityParameters = true
    ) {
      this.selectedAccount = selectedAccount
        ? new WebLoginAccountFactory(selectedAccount).getInstance()
        : undefined;
      this.persistSessionState();

      const parametersStore = useParametersStore();

      if (reloadEntityParameters && this.selectedAccount?.entityId) {
        await parametersStore.loadEntityParameterSettings(true);
      } else if (!this.selectedAccount?.entityId) {
        parametersStore.clearEntityParameterSettings();
      }
    },
    setUserProfileBasedSecurity(
      userProfileBasedSecurity: UserProfileBasedSecurity | undefined
    ) {
      this.userProfileBasedSecurity = userProfileBasedSecurity
        ? { ...userProfileBasedSecurity }
        : undefined;
      this.persistSessionState();
    },
    async loadUserProfileBasedSecurity() {
      const userProfileBasedSecurity = await getUserProfileBasedSecurity();
      this.setUserProfileBasedSecurity(userProfileBasedSecurity);
      return userProfileBasedSecurity;
    },
    restoreSession() {
      if (this.sessionRestored) {
        return;
      }

      const tokensJson =
        sessionStorage.getItem("tokens") ?? localStorage.getItem("tokens");

      if (!tokensJson) {
        this.sessionRestored = true;
        return;
      }

      try {
        const restoredTokens = parseStoredJson<WebLoginAuthenticationTokens>(
          tokensJson,
          "tokens"
        );
        const restoredWebLogin = getWebLoginFromJsonWebToken(
          restoredTokens.jsonWebToken
        );
        const storage = getStorage(restoredWebLogin.keepLoggedIn);
        const selectedAccountJson = storage.getItem("selectedAccount");
        const userProfileBasedSecurityJson = storage.getItem(
          "userProfileBasedSecurity"
        );

        this.tokens = restoredTokens;
        this.webLogin = restoredWebLogin;

        if (selectedAccountJson) {
          this.selectedAccount = new WebLoginAccountFactory(
            parseStoredJson<WebLoginAccount>(selectedAccountJson, "selectedAccount")
          ).getInstance();
        }

        if (userProfileBasedSecurityJson) {
          this.userProfileBasedSecurity =
            parseStoredJson<UserProfileBasedSecurity>(
              userProfileBasedSecurityJson,
              "userProfileBasedSecurity"
            );
        }

        this.sessionRestored = true;
      } catch (error) {
        console.error(
          "Failed to restore customer portal session from storage. Clearing stored session state.",
          error
        );
        this.clearState();
      }
    },
    clearState() {
      clearStorageKeys();
      this.selectedAccount = undefined;
      this.webLogin = {} as WebLogin;
      this.tokens = undefined;
      this.userProfileBasedSecurity = undefined;
      this.sessionRestored = true;

      const parametersStore = useParametersStore();
      parametersStore.clearEntityParameterSettings();
    },
  },
});
